import JSZip from 'jszip';

import { ordersDataService } from 'services';

import { downloadFile } from 'helpers/downloadFile';

/** Same prefix used everywhere else when resolving product images from S3. */
const IMAGE_KEY_PREFIX = 'catalogue/products';

/**
 * Shape returned by `BucketContext.getUrl(key)`. Mirrors `S3Item` from the
 * BucketContext but kept narrow here so the helper stays decoupled.
 */
type GetUrlResult = { url?: string; key?: string } | null | undefined;

type DownloadOrderExportParams = {
  orderId: string;
  lang: string;
  /** Used to build a human-friendly filename. Falls back to `orderId`. */
  orderNumber?: string | number;
  /**
   * When provided, the export is bundled with the referenced product images
   * into a zip archive. Typically the `getUrl` function from `BucketContext`,
   * which is loosely typed as `Function` upstream.
   */
  getUrl?: Function;
  /** Selected price type. Undefined means "No Price" — omit price columns. */
  priceType?: 'DISTRIBUTOR' | 'WHOLESALE' | 'RETAIL';
};

type ImageEntry = { name: string; blob: Blob };

/**
 * Resolves and downloads each image. Failures are logged and skipped so a
 * single missing object on S3 doesn't break the entire export.
 */
const fetchImages = async (imageKeys: string[], getUrl: Function): Promise<ImageEntry[]> => {
  const entries = await Promise.all(
    imageKeys.map(async (key): Promise<ImageEntry | null> => {
      try {
        const s3 = (await getUrl(`${IMAGE_KEY_PREFIX}${key}`)) as GetUrlResult;
        if (!s3?.url) return null;

        const res = await fetch(s3.url);
        if (!res.ok) return null;

        const blob = await res.blob();
        // Use only the basename — order is scoped to a single season so
        // style+fabric+color combinations are guaranteed unique.
        const name = key.split('/').pop() || key.replace(/^\/+/, '');
        return { name, blob };
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[downloadOrderExport] failed to load image', key, err);
        return null;
      }
    })
  );

  return entries.filter((entry): entry is ImageEntry => entry !== null);
};

/**
 * Calls the orders export endpoint and triggers a browser download.
 *
 * Backend response shape: `{ csv: string, imageUrls: string[] }`, where
 * `imageUrls` are relative S3 keys (e.g. `/IN26/MU10000000_5ZR_784.jpg`).
 *
 * When `getUrl` is supplied (the caller has access to `BucketContext`), the
 * helper resolves each key into a presigned URL, downloads every image, and
 * bundles them with the CSV inside a zip. Otherwise, or when the order has
 * no images, it falls back to a plain CSV download.
 */
const downloadOrderExport = async ({
  orderId,
  lang,
  orderNumber,
  getUrl,
  priceType,
}: DownloadOrderExportParams): Promise<void> => {
  const baseName = `COLMAR ORDER ${orderNumber ?? orderId}`;
  const withImages = Boolean(getUrl);

  const res = await ordersDataService.export(orderId, lang, withImages, priceType);
  const { csv = '', imageUrls = [] } = res.data ?? {};

  if (!withImages || !getUrl || imageUrls.length === 0) {
    downloadFile({ data: csv, filename: `${baseName}.csv` });
    return;
  }

  // Deduplicate keys — the same style/colour can appear on multiple lines.
  const uniqueKeys = Array.from(new Set(imageUrls));
  const images = await fetchImages(uniqueKeys, getUrl);

  const zip = new JSZip();
  zip.file(`${baseName}.csv`, csv);
  const imagesFolder = zip.folder('images');
  images.forEach(({ name, blob }) => imagesFolder?.file(name, blob));

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  downloadFile({
    data: zipBlob,
    filename: `${baseName}.zip`,
    mimeType: 'application/zip',
  });
};

export { downloadOrderExport };
export type { DownloadOrderExportParams };
